/**
 * filterArRowIdsForRecipient
 *
 * The single definition of "a recipient's rows" for the fan-out pre-flight
 * (SYD-2410): what one table contributes to one recipient's document.
 * `useRecipientRowCounts` is its only consumer — it sums what survives here per
 * target, and `WorkflowParameterContent` refuses a recipient whose sum is zero
 * rather than mailing them a blank document.
 *
 * The three narrowings are the ones both builders apply, in their order: the
 * split that scopes the document to this recipient, the grid's per-column
 * filters — the export portal shares the on-screen table's slot, so a filter
 * typed into the grid narrows the exported PDF and XLSX too — and the ticked
 * selection.
 *
 * Two deliberate departures from the filters it composes. A table whose columns
 * lack the split column yields `[]`, where `filterArRowIdsBySplit` alone keeps
 * every row: here a `null` split is missing evidence, not "no filtering", and an
 * unfiltered table must never vouch for a recipient it says nothing about. And
 * bundle headers are never re-admitted — admitting a kit title on behalf of a
 * ticked member is presentation, so a header whose members are all unticked
 * counts as empty.
 */

import type { ArColumnDefinition, ArRowData, ArRowId, SplitQuery, TypeId } from '#types';
import type { DocEntry } from '@/store';
import { filterArRowIdsBySelection } from '@/components/ArTable/export/filterArRowIdsBySelection';
import { filterArRowIdsByColumnFilters } from '@/components/ArTable/filter/filterArRowIdsByColumnFilters';
import { resolveSplitForTable } from './resolveSplitForTable';
import { filterArRowIdsBySplit } from './filterArRowIdsBySplit';

type FilterArRowIdsForRecipientParams = {
  rowIds: readonly ArRowId[];
  rowsDocs: ReadonlyMap<string, DocEntry<ArRowData>>;
  /** The table type's own columns, unnarrowed. */
  columns: readonly ArColumnDefinition[];
  splitQuery: SplitQuery;
  /** The grid's active per-column filters as `[typeId, value]` pairs; empty means none. */
  filterEntries: ReadonlyArray<readonly [TypeId, string]>;
  /** `null` or empty means no selection — every row surviving the rest is kept. */
  selectedRowIds: readonly ArRowId[] | null;
};

function filterArRowIdsForRecipient({
  rowIds,
  rowsDocs,
  columns,
  splitQuery,
  filterEntries,
  selectedRowIds,
}: FilterArRowIdsForRecipientParams): ArRowId[] {
  const split = resolveSplitForTable({ columns, splitQuery });
  if (!split) return [];

  return filterArRowIdsBySelection({
    rowIds: filterArRowIdsByColumnFilters({
      rowIds: filterArRowIdsBySplit({ rowIds, rowsDocs, split }),
      rowsDocs,
      filterEntries,
    }),
    selectedRowIds,
  });
}

export { filterArRowIdsForRecipient };
